import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { useEffect } from 'react';
import { Button } from 'primereact/button';
import { formatDate } from '../../../utils/helper';
import { Dropdown } from 'primereact/dropdown';
import { TabMenu } from 'primereact/tabmenu';
import { MatenFilter, MatenTable, MatenTableAction, MatenTableColumn } from '../../../shared';
import KnowledgeOverviewStore from '../stores/KnowledgeOverviewStore';
import KnowledgeDialogStore from '../stores/KnowledgeDialogStore';

// Define knowledge type options locally
const knowledgeTypeOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Modul', value: 'module' },
];

const StoreTypeTabs = observer(({ store }: { store: any }) => {
    const allStores = store?.allKnowledgeStores || [];
    const infoText = store?.currentKnowledgeStore?.short_description || null;

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: '1rem',
                gap: '1rem',
            }}
        >
            <TabMenu
                model={allStores}
                activeIndex={store?.currentKnowledgeStoreIndex || 0}
                onTabChange={(event: any) => {
                    const { id } = event?.value || {};
                    store.switchStore(id);
                }}
            />
            {infoText && (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#e8f4fd',
                        color: '#1976d2',
                        borderRadius: '6px',
                        padding: '0.4rem 1rem',
                        fontWeight: 600,
                        fontSize: '1rem',
                        boxShadow: '0 1px 4px rgba(25, 118, 210, 0.07)',
                        minWidth: 0,
                        flex: '1 1 250px',
                        wordBreak: 'break-word',
                    }}
                >
                    <i
                        className="pi pi-info-circle"
                        style={{ marginRight: 8, fontSize: '1.2rem' }}
                    />
                    {infoText}
                </span>
            )}
        </div>
    );
});

const KnowledgeOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(KnowledgeOverviewStore);
    }, [sdk]);

    // Define columns for knowledge
    const columns: MatenTableColumn[] = [
        {
            field: 'image',
            header: '',
            sortable: false,
            style: { textAlign: 'center' },
            body: (rowData: any) =>
                rowData.image ? (
                    <img
                        src={rowData.image}
                        alt="Knowledge"
                        style={{ width: '50px', height: '50px', borderRadius: '10%' }}
                    />
                ) : (
                    <i
                        className={`pi ${rowData.icon || 'pi-database'}`}
                        style={{ fontSize: '2rem', textAlign: 'center' }}
                    />
                ),
        },
        {
            field: 'id',
            header: 'ID',
            sortable: true,
        },
        {
            field: 'name',
            header: 'Name',
            sortable: true,
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created),
        },
        {
            field: 'updated',
            header: 'Aktualisiert',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.updated),
        },
        {
            field: 'trained',
            header: 'Angelernt',
            sortable: true,
            body: (rowData: any) => {
                const hasUpdates =
                    rowData.updated && rowData.trained && rowData.updated !== rowData.trained;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{formatDate(rowData.trained, false, '-')}</span>
                        {hasUpdates && (
                            <i
                                className="pi pi-exclamation-triangle"
                                style={{
                                    color: '#ff9800',
                                    fontSize: '1rem',
                                }}
                                title="Aktualisierungen verfügbar - Training erforderlich"
                            />
                        )}
                    </div>
                );
            },
        },
    ];

    // Define actions for knowledge
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Wissen bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(KnowledgeDialogStore, {
                    type: rowData.type,
                    knowledge_id: rowData.id,
                });
            },
            onDisabled: (rowData: any) => rowData.type !== 'module',
            testId: 'edit-knowledge-btn',
        },
        {
            icon: 'pi pi-trash',
            tooltip: 'Wissen löschen',
            tooltipPosition: 'left',
            onClick: async (rowData: any) => {
                const result = await sdk.getDialogDelete({
                    title: 'Wissen löschen',
                    message: `Sind Sie sicher, dass Sie das Wissensmodul "${rowData.name}" löschen möchten?`,
                    searchWord: 'löschen',
                    onConfirm: async () => sdk.currentStore.onDeleteKnowledge(rowData),
                });

                if (result.confirmed) {
                    sdk.showSuccessToast('Wissen erfolgreich gelöscht');
                }
            },
            testId: 'delete-knowledge-btn',
            className: 'p-button-danger',
        },
    ];

    // Additional header content
    const headerContent = (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
                label="Neu"
                icon="pi pi-plus"
                onClick={() =>
                    sdk.openDialog(KnowledgeDialogStore, {
                        type: 'module',
                        store_id: sdk.currentStore?.selectedStore || null,
                    })
                }
                style={{ marginRight: '0.5rem' }}
                data-testid="add-knowledge-module-btn"
            />
        </div>
    );

    const tableFilter: MatenFilter[] = [
        {
            title: 'Wissens-Typ',
            property: 'filterType',
            removeFilter: (store: any) => {
                store.applyFilterType(null);
            },
            chipContent: (filter: any) =>
                filter
                    ? `Wissens-Typ: ${knowledgeTypeOptions.find((option: any) => option.value === filter)?.label || filter}`
                    : '',
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterType || null}
                    options={knowledgeTypeOptions}
                    onChange={(e: any) => store.applyFilterType(e.value)}
                    placeholder="Wissens-Typ auswählen"
                    optionLabel="label"
                    style={{ width: '100%' }}
                    data-testid="knowledge-type-filter-dropdown"
                />
            ),
        },
    ];

    const allKnowledgeStores = sdk.currentStore?.allKnowledgeStores;
    const allKnowledgeModules = sdk.currentStore?.allKnowledgeModules;
    const showNotConfigured =
        !sdk.currentStore?.listLoading && allKnowledgeStores && allKnowledgeStores.length === 0;

    return (
        <>
            <StoreTypeTabs store={sdk.currentStore} />
            {showNotConfigured ? (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '3rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        margin: '2rem 0',
                    }}
                >
                    <i
                        className="pi pi-cog"
                        style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}
                    />
                    <h3 style={{ color: '#495057', marginBottom: '1rem' }}>Modul konfigurieren</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1.5rem', fontSize: '1.1rem' }}>
                        Bevor Sie Wissensinhalte verwalten können, müssen Sie zuerst das KI-Hub
                        Modul konfigurieren.
                    </p>
                </div>
            ) : (
                <MatenTable
                    data={allKnowledgeModules}
                    store={sdk.currentStore}
                    columns={columns}
                    actions={actions}
                    filter={tableFilter}
                    search={{
                        placeholder: 'Wissen durchsuchen',
                    }}
                    emptyMessage="Keine Wissensinhalte gefunden"
                    entity={{ singular: 'Wissensinhalt', plural: 'Wissensinhalte' }}
                    testId={'knowledge-overview'}
                    toolbarContent={headerContent}
                    pagination={{ rows: 30 }}
                />
            )}
        </>
    );
});

export default KnowledgeOverview;
